import * as XLSX from "xlsx";
import {
  getCompanyMasterPagination,
  getProfileDetails,
} from "../Components/CompanyProfileNew/Utility";
import { Urls } from "../Urls";
import mixpanel from "mixpanel-browser";
import { toast } from "react-toastify";
import { masterConfigs } from "./downloadMasterConfigs";
import axios from "../Axios/Axios";

/**
 * Fetches freight master data.
 *
 * @param {number} totalCount - total number of records to fetch. Default is -1 for no limit.
 * @returns {Promise<Array>} - promise that resolves to freight master data.
 * @throws {Error} - Throws an error if the fetch operation fails.
 */
export const getFreightMasterData = async (companyId, totalCount = -1) => {
  try {
    const response = await axios.post(
      `${Urls.freight_master}?action=fetch_freight_page`,
      {
        resource: "freight_master",
        date_range: [],
        filtersOperator: "and",
        filters: [],
        company_id: companyId,
        pagination: {
          skip: 0,
          limit: totalCount,
        },
        sort: {
          key: "created_on",
          order: "desc",
        },
      }
    );
    return response.data.lane;
  } catch (error) {
    console.error("Error fetching freight master data:", error);
    throw error;
  }
};

/**
 * Fetches master details based on the type.
 *
 * @param {string} type - type of master data to fetch.
 * @param {string} companyId - company ID
 * @param {number} totalCount - total number of records to fetch. Default is -1 for no limit.
 * @returns {Promise<Array>} - A promise that resolves to master details.
 */
async function fetchMasterDetails(type, companyId, totalCount = -1) {
  if (type === "truck") {
    const res = await getProfileDetails(companyId, "truck_details", "company");
    return res.data?.[0]?.truck_details;
  } else if (type === "freight") {
    return await getFreightMasterData(companyId, totalCount);
  } else if (type === "location" || type === "vendor" || type === "material" || type === "route") {
    let sortKey = type === "vendor" ? "transporter_name" : type === "material" ? "material_code" : type === "route" ? "route_code" : "location";
    const payload = {
      filter_opertaion: "and",
      date_range: [],
      filters: [],
      search: "",
      pagination: { skip: 0, limit: totalCount === -1 ? 50000 : totalCount },
      sort: { key: sortKey, order: "asc" },
    };
    const response = await axios.post(
      `${Urls.companyMaster}?master_type=${type}&action=fetch_masters_data`,
      payload
    );
    return response.data?.[0]?.master_details ?? response.data?.master_details ?? response.data;
  } else {
    const res = await getCompanyMasterPagination(companyId, type, 0, -1);
    return res?.data?.master_details ?? res?.data?.[0]?.master_details;
  }
}

/**
 * Prepares data for Excel export based on the master details and type.
 *
 * @param {string} type - type of master data
 * @param {Array} masterDetails - array of master details
 * @returns {{ headers: Array, data: Array }} - object containing headers and data for the Excel file.
 */
function prepareExcelData(type, masterDetails) {
  const config = masterConfigs[type];

  const headers = config.getHeaders
    ? config.getHeaders(masterDetails)
    : config.headers;

  const maxItems =
    type === "truck"
      ? Math.max(...masterDetails.map((item) => item.trucks?.length ?? 0))
      : type === "freight"
        ? Math.max(
          ...masterDetails.map((item) => item.transporter_details?.length ?? 0)
        )
        : 0;

  const data = masterDetails.map((item, index) =>
    type === "truck" || type === "freight"
      ? config.mapData(item, index, maxItems)
      : config.mapData(item, index)
  );

  return { headers, data };
}

/**
 * Generates an Excel file from the headers and data.
 *
 * @param {Array} headers - headers for the Excel file.
 * @param {Array} data - data to include in the Excel file.
 * @param {string} type - type of master data for naming the file.
 */
function generateExcelFile(headers, data, type) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${type}Master.xlsx`);
}

/**
 * Tracks a Mixpanel event for downloading master data.
 *
 * @param {string} type - type of master data being downloaded.
 */
function trackMixpanelEvent(type) {
  mixpanel.track(`User has clicked on ${type} master download button`, {
    ApplicationName: `${Urls.mixpanelappname}`,
    ScreenName: "LoRRI-CompanyProfile",
    ModuleName: "edit_page",
    ActivityLog: `userClickedDownloadButton`,
    Data: `None`,
    Definition: `User has clicked on ${type} master download button`,
    ActivityType: "event_view",
    UserName: `${localStorage.getItem("userdetails")}`,
    UserEmail: `${localStorage.getItem("useremail")}`,
    CompanyName: `${localStorage.getItem("companyname")}`,
  });
}

/**
 * starts download for the master type.
 *
 * @param {string} type - type of master
 * @param {string} companyId - company id
 * @param {number} totalCount - total number of records to fetch. Default is -1
 * @returns {Promise<void>} - promise that resolves when the download is complete.
 * @throws {Error} - Throws an error if the download fails
 */
export async function downloadAction(type, companyId, totalCount = -1) {
  try {
    if (!masterConfigs[type]) {
      throw new Error(`Invalid master type: ${type}`);
    }

    const masterDetails = await fetchMasterDetails(type, companyId, totalCount, "company");

    if (!masterDetails || masterDetails.length === 0) {
      toast.info("No data available for download");
      return;
    }

    const { headers, data } = prepareExcelData(type, masterDetails);
    generateExcelFile(headers, data, type);
    trackMixpanelEvent(type);
  } catch (error) {
    console.error("Download failed:", error);
    toast.info("Download failed. Please try again later.");
  }
}
